import React, { useState } from 'react';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import Icon from '../Icons';
import './styles.css';

const CookieManagementModal = () => {
  const { 
    showManagement, 
    consent, 
    updateConsent, 
    acceptAll, 
    acceptEssential, 
    closeManagement 
  } = useCookieConsent();

  const [localConsent, setLocalConsent] = useState(consent);

  const handleToggle = (category) => {
    if (category === 'essential') return; // Essential cookies cannot be disabled
    
    setLocalConsent(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    updateConsent(localConsent);
    closeManagement();
  };

  const handleAcceptAll = () => {
    acceptAll();
  };

  const handleEssentialOnly = () => {
    acceptEssential();
  };

  if (!showManagement) return null;

  const cookieCategories = [
    {
      id: 'essential',
      title: 'Essential Cookies',
      description: 'These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you which amount to a request for services.',
      examples: ['Authentication', 'Security', 'Load balancing', 'User interface preferences'],
      required: true
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      description: 'These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third party providers whose services we have added to our pages.',
      examples: ['Language preferences', 'Region selection', 'Accessibility options', 'User interface customization'],
      required: false
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      description: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular.',
      examples: ['Google Analytics', 'Page views', 'User behavior', 'Performance metrics'],
      required: false
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      description: 'These cookies may be set through our site by our advertising partners to build a profile of your interests and show you relevant adverts on other sites.',
      examples: ['Advertising networks', 'Social media integration', 'Remarketing', 'Interest-based advertising'],
      required: false
    },
    {
      id: 'preferences',
      title: 'Preference Cookies',
      description: 'These cookies enable the website to remember choices you make and provide enhanced, more personal features such as personalized content and recommendations.',
      examples: ['Content recommendations', 'Personalized settings', 'User preferences', 'Customized experience'],
      required: false
    }
  ];

  return (
    <div className="cookie-management-overlay" onClick={closeManagement}>
      <div className="cookie-management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cookie-modal-header">
          <div className="cookie-modal-title">
            <Icon name="settings" size={24} />
            <h2>Cookie Preferences</h2>
          </div>
          <button className="cookie-modal-close" onClick={closeManagement}>
            <Icon name="x" size={20} />
          </button>
        </div>

        <div className="cookie-modal-content">
          <div className="cookie-intro">
            <p>
              We use cookies to enhance your experience, analyze site traffic, and personalize content. 
              You can choose which categories of cookies to allow. Essential cookies are required for 
              the website to function properly.
            </p>
          </div>

          <div className="cookie-categories">
            {cookieCategories.map((category) => (
              <div key={category.id} className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h3>{category.title}</h3>
                    <span className={`cookie-status ${localConsent[category.id] ? 'enabled' : 'disabled'}`}>
                      {localConsent[category.id] ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={localConsent[category.id]}
                      onChange={() => handleToggle(category.id)}
                      disabled={category.required}
                    />
                    <span className="cookie-slider"></span>
                  </label>
                </div>
                
                <div className="cookie-category-content">
                  <p>{category.description}</p>
                  <div className="cookie-examples">
                    <strong>Examples:</strong>
                    <ul>
                      {category.examples.map((example, index) => (
                        <li key={index}>{example}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="cookie-modal-footer">
          <div className="cookie-quick-actions">
            <button 
              className="cookie-btn cookie-btn-secondary"
              onClick={handleEssentialOnly}
            >
              <Icon name="shield" size={16} />
              Essential Only
            </button>
            
            <button 
              className="cookie-btn cookie-btn-primary"
              onClick={handleAcceptAll}
            >
              <Icon name="check" size={16} />
              Accept All
            </button>
          </div>
          
          <button 
            className="cookie-btn cookie-btn-save"
            onClick={handleSave}
          >
            <Icon name="save" size={16} />
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieManagementModal;
